import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import _ from "lodash";
import ModalBasicLayout from "../../../Modal/ModalBasicLayout";
import Loader from "../../../Loaders/Loader";
import Button from "../../../Buttons/Button";
import { SimpleAlert } from "../../../Alerts/Alerts";
import { PopupBlockedGuideAlert } from "../../../Alerts/PopupBlockedGuideAlert";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import { usePagination } from "../../../../hooks/usePagination";
import useNearScreen from "../../../../hooks/useNearScreen";
import {
  startGetInstantForm,
  startGetTikTokInstantPageEditorToken,
} from "../../../../actions/adsCampaigns";
import { openTikTokInstantPageEditor } from "../../../../utils/tiktokInstantPage";
import AdMediaPreviewTooltipContent, {
  adMediaHoverPreviewHeight,
  adMediaPreviewTooltipSlotProps,
} from "../../../Common/AdMediaPreviewTooltipContent";
import {
  ALERT_ICON_TYPE_ERROR,
  BORDER_RADIUS,
  TIKTOK,
} from "../../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import {
  BUTTON_CREATE,
  BUTTON_SELECT,
  BUTTON_UPDATE,
  LOADING_INFORMATION,
  MODAL_INSTANT_FORMS_EMPTY_TEXT,
  MODAL_INSTANT_FORMS_LIST_DESCRIPTION,
  MODAL_INSTANT_FORMS_LIST_TITLE,
  TIKTOK_INSTANT_FORM_EDITOR_FAILED,
  INTEGRATION_MISSING_PERMISSION,
  UPDATING_INFORMATION,
} from "../../../../i18n/keysTranslations";

import { LaunchError } from "../../../../utils/errors";

const ModalTikTokInstantForms = ({
  modalOpen,
  integrationID,
  adAccountID,
  language,
  onCloseModal = () => {},
  onCallbackSelect = () => {},
}) => {
  const [instantForms, setInstantForms] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [listError, setListError] = useState("");

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onGetInstantForms = async () => {
    if (loadLastItem) return;

    const response = await dispatch(
      startGetInstantForm({
        integrationID,
        adAccountID,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem,
        platform: TIKTOK,
      })
    );

    if (response.ok) {
      setListError("");
      setInstantForms((prev) => _.uniqBy([...prev, ...response.data], "id"));
    } else if (response.code === INTEGRATION_MISSING_PERMISSION) {
      onChangeLoadLastItem(true);
      setListError(response.message || "");
    }

    if (isLoading) setIsLoading(false);
    return response.ok;
  };

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: 10,
    onCallBackNextPage: onGetInstantForms,
  });

  const { fromRef, loadLastItem, onChangeLoadLastItem, isNearScreen } =
    useNearScreen({
      distance: "100px",
    });

  const onUpdateInstantForms = async () => {
    setSelectedItem(null);
    setIsUpdating(true);
    setIsLoading(true);

    const response = await dispatch(
      startGetInstantForm({
        integrationID,
        adAccountID,
        onChangeLastKey,
        onChangeLoadLastItem,
        platform: TIKTOK,
      })
    );

    if (response.ok) {
      setListError("");
      setInstantForms(response.data || []);
    } else if (response.code === INTEGRATION_MISSING_PERMISSION) {
      onChangeLoadLastItem(true);
      setInstantForms([]);
      setListError(response.message || "");
    }

    setIsLoading(false);
    setIsUpdating(false);
    return response.data || [];
  };

  useEffect(() => {
    onGetInstantForms();
    // eslint-disable-next-line
  }, [isNearScreen]);

  const onSendSelectedItem = () => {
    const instantFormSelected = instantForms.find(
      (instantForm) => instantForm.id === selectedItem
    );
    if (!instantFormSelected) return;
    onCallbackSelect(instantFormSelected);
    onCloseModal();
  };

  const onCreateInstantForm = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const tokenResponse = await dispatch(
        startGetTikTokInstantPageEditorToken({
          integrationID,
          adAccountID,
        })
      );
      setIsCreating(false);
      if (!tokenResponse?.ok) {
        if (tokenResponse?.code === INTEGRATION_MISSING_PERMISSION) {
          setListError(tokenResponse.message || "");
        } else {
          SimpleAlert({
            text: t(TIKTOK_INSTANT_FORM_EDITOR_FAILED),
            icon: ALERT_ICON_TYPE_ERROR,
          });
        }
        return;
      }

      let created;
      try {
        created = await openTikTokInstantPageEditor({
          appId: tokenResponse.data?.appId,
          advertiserId: tokenResponse.data?.advertiserId || adAccountID,
          tipSdkAccessToken: tokenResponse.data?.tipSdkAccessToken,
          language,
        });
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "onCreateInstantForm",
            capture: "exception",
          })
        );
        if (error?.code === "popupBlocked") {
          PopupBlockedGuideAlert();
        } else {
          SimpleAlert({
            text: t(TIKTOK_INSTANT_FORM_EDITOR_FAILED),
            icon: ALERT_ICON_TYPE_ERROR,
          });
        }
        return;
      }

      const refreshed = await onUpdateInstantForms();
      const forms = Array.isArray(refreshed) ? refreshed : [];
      const matched = forms.find(
        (form) => String(form.id) === String(created.id)
      );
      if (matched) {
        setSelectedItem(matched.id);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const instantFormsSorted = [...instantForms].sort(
    (a, b) => (b.creationTime || 0) - (a.creationTime || 0)
  );

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="xs"
      title={t(MODAL_INSTANT_FORMS_LIST_TITLE)}
      description={t(MODAL_INSTANT_FORMS_LIST_DESCRIPTION)}
    >
      {isLoading && (
        <Loader
          hasMessage={true}
          message={t(isUpdating ? UPDATING_INFORMATION : LOADING_INFORMATION)}
        />
      )}
      {!isLoading && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1,
              justifyContent: "flex-end",
              alignItems: "flex-end",
              width: "100%",
              mb: 2,
            }}
          >
            <Button
              type="button"
              color="secondary"
              onClick={onUpdateInstantForms}
              disabled={isUpdating}
              sx={{
                minWidth: 110,
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: "13px !important",
              }}
            >
              {t(BUTTON_UPDATE)}
            </Button>
            <Button
              type="button"
              color="secondary"
              onClick={onCreateInstantForm}
              loading={isCreating}
              sx={{
                minWidth: 110,
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: "13px !important",
              }}
            >
              {t(BUTTON_CREATE)}
            </Button>
          </Box>
          {listError && instantFormsSorted.length > 0 && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center", px: 2, mb: 2 }}
            >
              {listError}
            </Typography>
          )}
          {instantFormsSorted.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 160,
              }}
            >
              <Typography
                variant="body2"
                color={listError ? "error" : "text.secondary"}
                sx={{ textAlign: "center", px: 2 }}
              >
                {listError || t(MODAL_INSTANT_FORMS_EMPTY_TEXT)}
              </Typography>
            </Box>
          ) : (
            <RadioGroup
              value={selectedItem || ""}
              onChange={(event) => setSelectedItem(event.target.value)}
            >
              {instantFormsSorted.map((instantForm, index) => {
                const isLastItem = index === instantFormsSorted.length - 1;
                return (
                  <Box
                    key={instantForm.id}
                    ref={isLastItem ? fromRef : undefined}
                    sx={{
                      mb: 1,
                      px: 1.5,
                      py: 1.5,
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                      borderRadius: BORDER_RADIUS,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <FormControlLabel
                      value={instantForm.id}
                      control={<Radio size="small" />}
                      label={
                        <Typography
                          variant="body2"
                          sx={{
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {instantForm.name || instantForm.id}
                        </Typography>
                      }
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        m: 0,
                        ml: 0,
                        mr: 0,
                        alignItems: "flex-start",
                        gap: 1,
                        "& .MuiRadio-root": { p: 0, m: 0, mt: "1px" },
                      }}
                    />
                    {instantForm.thumbnail ? (
                      <Tooltip
                        placement="left"
                        slotProps={adMediaPreviewTooltipSlotProps}
                        title={
                          <AdMediaPreviewTooltipContent
                            mediaUrls={[instantForm.thumbnail]}
                            height={adMediaHoverPreviewHeight}
                          />
                        }
                        arrow
                      >
                        <Box
                          component="img"
                          src={instantForm.thumbnail}
                          alt=""
                          sx={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 1,
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </Box>
                );
              })}
            </RadioGroup>
          )}
          {!loadLastItem && instantFormsSorted.length > 0 && (
            <Loader size={20} />
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="button"
              onClick={onSendSelectedItem}
              disabled={!selectedItem}
            >
              {t(BUTTON_SELECT)}
            </Button>
          </Box>
        </>
      )}
    </ModalBasicLayout>
  );
};

export default ModalTikTokInstantForms;
